import type { CheckboxGroupType, SelectOptionType } from '~/presentation/reports/ui/type';
import type { IRItemCard, IRItemInfo } from '../types';
import type { IControlSettingUI } from '../../type';

import {
  DEVICE_OPTIONS,
  TRAFFIC_SOURCE_OPTIONS,
  VERSION_OPTIONS,
  VISITOR_OPTIONS,
  OPERATOR_OPTIONS,
} from '~/modules/report-analytic';
import { SelectVariantType } from '~/presentation/reports/ui/type';
import { createOperatorFilter } from '../utils/FilterUI';
import { EReportControlId, ERFilterKey } from '../types';
import { EControlType } from '../../type';

export const GENERAL_FILTER_INFO: IRItemCard = {
  title: 'Filters',
  content:
    'Conditions to narrow down the data, like orders from certain regions or sales above a certain amount. Use filters to focus your analysis.',
  helps: [],
};

export const REPORT_FILTER_INFO: IRItemInfo = {
  [ERFilterKey.DEVICE]: {
    title: 'Device',
    content: 'View data from visitors using specific devices',
    helps: [],
  },
  [ERFilterKey.TRAFFIC_SOURCE]: {
    title: 'Traffic source',
    content: 'View data from where visitors arrived at your store',
    helps: [],
  },
  [ERFilterKey.VISITOR]: {
    title: 'Visitor',
    content: 'View data from specific visitor type (e.g. New or Returning)',
    helps: [],
  },
  [ERFilterKey.EXPERIMENT]: {
    title: 'Experiment',
    content: 'View data collected from a specific A/B testing experiment',
    helps: [],
  },
  [ERFilterKey.PAGE]: {
    title: 'Page',
    content: 'View data from a specific page within your experiment or store',
    helps: [],
  },
  [ERFilterKey.VERSION]: {
    title: 'Version',
    content: 'View data from a specific template version',
    helps: [],
  },
};

const REPORT_FILTER_OPTIONS: SelectOptionType[] = [
  {
    label: 'Device',
    value: ERFilterKey.DEVICE,
  },
  {
    label: 'Traffic source',
    value: ERFilterKey.TRAFFIC_SOURCE,
  },
  {
    label: 'Visitor',
    value: ERFilterKey.VISITOR,
  },
  {
    label: 'Experiment',
    value: ERFilterKey.EXPERIMENT,
  },
  {
    label: 'Page',
    value: ERFilterKey.PAGE,
  },
  {
    label: 'Version',
    value: ERFilterKey.VERSION,
  },
];

export const REPORT_FILTER_GROUP: CheckboxGroupType = [
  {
    title: '',
    options: REPORT_FILTER_OPTIONS,
  },
];

export const FILTER_SETTING_UI: Record<string, IControlSettingUI> = {
  [ERFilterKey.DATE]: {
    id: ERFilterKey.DATE,
    label: 'Date range',
    type: EControlType.SELECT_COMPOUND,
    configs: [{ id: 'date', variant: SelectVariantType.DATE }],
  },
  ...createOperatorFilter({
    id: ERFilterKey.DEVICE,
    label: 'Device',
    operatorId: EReportControlId.DEVICE_OPERATOR,
    singleValueId: EReportControlId.DEVICE,
    multiValueId: EReportControlId.DEVICES,
    options: DEVICE_OPTIONS,
  }),
  ...createOperatorFilter({
    id: ERFilterKey.VISITOR,
    label: 'Visitor',
    operatorId: EReportControlId.VISITOR_OPERATOR,
    singleValueId: EReportControlId.VISITOR,
    multiValueId: EReportControlId.VISITORS,
    options: VISITOR_OPTIONS,
  }),

  ...createOperatorFilter({
    id: ERFilterKey.TRAFFIC_SOURCE,
    label: 'Traffic Source',
    operatorId: EReportControlId.TRAFFIC_SOURCE_OPERATOR,
    singleValueId: EReportControlId.TRAFFIC_SOURCE,
    multiValueId: EReportControlId.TRAFFIC_SOURCES,
    options: TRAFFIC_SOURCE_OPTIONS,
  }),
  ...createOperatorFilter({
    id: ERFilterKey.VERSION,
    label: 'Version',
    operatorId: EReportControlId.VERSION_OPERATOR,
    singleValueId: EReportControlId.VERSION,
    multiValueId: EReportControlId.VERSIONS,
    options: VERSION_OPTIONS,
  }),
  ...createOperatorFilter({
    id: ERFilterKey.PAGE,
    label: 'Page',
    operatorId: EReportControlId.PAGE_OPERATOR,
    singleValueId: EReportControlId.SINGLE_PAGE,
    multiValueId: EReportControlId.LIST_PAGE,
    enableCreateOption: true,
    options: [],
    type: EControlType.SELECT_PAGE,
    singleOperatorOnly: false,
  }),
  [ERFilterKey.EXPERIMENT]: {
    id: ERFilterKey.EXPERIMENT,
    uid: ERFilterKey.EXPERIMENT,
    label: 'Experiment',
    type: EControlType.SELECT_CAMPAIGN,
    info: REPORT_FILTER_INFO[ERFilterKey.EXPERIMENT],
    dependentControls: [
      {
        id: EReportControlId.FILTER,
      },
    ],
    configs: [
      {
        id: EReportControlId.TAB_CAMPAIGN,
        tabs: [
          { id: EReportControlId.SINGLE_CAMPAIGN, content: 'Template' },
          { id: EReportControlId.GROUP_CAMPAIGN, content: 'Multipage' },
          { id: EReportControlId.PRICE_CAMPAIGN, content: 'Product price' },
          { id: EReportControlId.URL_CAMPAIGN, content: 'URL' },
        ],
        defaultTab: EReportControlId.SINGLE_CAMPAIGN,
        variant: SelectVariantType.ACTION_LIST,
        isShowSearch: true,
      },
    ],
    controls: [
      {
        id: EReportControlId.FILTER_GROUP_CAMPAIGN_ITEM,
        label: 'Template',
        type: EControlType.SELECT_CAMPAIGN_ITEM,
        condition: `!${EReportControlId.GROUP_CAMPAIGN}`,
        dependentControls: [
          {
            id: EReportControlId.GROUP_CAMPAIGN,
          },
        ],
        configs: [
          {
            id: EReportControlId.CAMPAIGN_ITEM_OPERATOR,
            variant: SelectVariantType.ACTION_LIST,
            options: OPERATOR_OPTIONS,
            defaultValue: OPERATOR_OPTIONS?.[1]?.value,
          },
          {
            id: EReportControlId.GROUP_CAMPAIGN_ITEM,
            options: [],
            variant: SelectVariantType.ACTION_LIST,
            condition: `${EReportControlId.CAMPAIGN_ITEM_OPERATOR} === 'is_one_of'`,
          },
          {
            id: EReportControlId.GROUP_CAMPAIGN_ITEMS,
            options: [],
            defaultValue: [],
            variant: SelectVariantType.CHOICE,
            condition: `${EReportControlId.CAMPAIGN_ITEM_OPERATOR} === 'is'`,
          },
        ],
      },
    ],
  },
};
